/**
 * เพื่อน and คู่แค้น: the friends list and the sworn-enemies list (data/social.ts), saved in `progress.social`.
 *
 * - **Friends:** `friendAdd` names someone. Offline the simulated players (rivals, the arena roster's people in this
 *   world) say yes at once; online the server sends the request and fills the other side's `requests`. `friendAccept`
 *   / `friendDecline` answer a waiting request, `friendRemove` drops a friend.
 * - **คู่แค้น:** when a player with PK on (เปิดแดง) or a red/purple name kills the player, `enemyOffer` offers to swear
 *   them as an enemy; `enemyAdd` (within `SOCIAL.offerWindow`) does, keeping who, when and how many kills. A sworn
 *   enemy who kills the player again adds to the count by itself. `enemyRemove` forgives.
 * - **In view:** a sworn enemy within `SOCIAL.nearRange` m gives `enemyNear` once, until they leave it again
 *   (the client's alert and the icon over their head).
 */
import { PK } from '../karma';
import { newSocial, relationOf, SOCIAL, type SocialSave } from '../data/social';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`). */
export type SocialCommand =
  | { t: 'friendAdd'; name: string }
  | { t: 'friendAccept'; name: string }
  | { t: 'friendDecline'; name: string }
  | { t: 'friendRemove'; name: string }
  | { t: 'enemyAdd'; name: string }
  | { t: 'enemyRemove'; name: string };

/** Events (part of `WorldEvent`). */
export type SocialEvent =
  /** A red name killed the player: offer to swear them (`known`: already sworn, the count went up). */
  | { type: 'enemyOffer'; name: string; known: boolean; kills: number }
  /** A sworn enemy came into view. */
  | { type: 'enemyNear'; id: number; name: string; x: number; y: number }
  /** The lists changed. */
  | { type: 'social'; what: 'friendAdded' | 'friendRemoved' | 'friendDeclined' | 'enemyAdded' | 'enemyRemoved'; name: string };

const SYSTEMS = new WeakMap<object, SocialSystem>();
/** The World's social system (the client, tests). */
export const socialOf = (w: object): SocialSystem | undefined => SYSTEMS.get(w);

/** Someone who plays (a player, or a simulated one) rather than a beast or a townsperson. */
const isPlayerLike = (e: Entity) => e.kind === 'player' || e.kind === 'rival';
/** PK on, or a red or purple name. */
export const isRedName = (e: Entity) => e.pk || e.pkPoints > PK.red;

export class SocialSystem implements WorldSystem {
  readonly name = 'social';
  /** The last player-like attacker of the player: id, name, red at the blow, and when. */
  private lastBlow: { id: number; name: string; red: boolean; at: number } | null = null;
  /** Red killers the player may still swear, by name: when the offer was made. */
  private readonly offers = new Map<string, number>();
  /** Sworn enemies in view now (entity ids). */
  private readonly inView = new Set<number>();
  private nearT = 0;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** The lists (made on first use). */
  get save(): SocialSave {
    const pr = this.h.world.progress;
    return (pr.social ??= newSocial());
  }

  /** Names the player may swear now (offers still open). */
  openOffers(): string[] {
    const now = this.h.world.time;
    return [...this.offers].filter(([, t]) => now - t <= SOCIAL.offerWindow).map(([n]) => n);
  }

  /** Whether a sworn enemy is in view now. */
  inSight(id: number): boolean {
    return this.inView.has(id);
  }

  /** About now, in epoch seconds: the save's clock plus the time played since. */
  private clock(): number {
    const w = this.h.world;
    return Math.floor(w.progress.savedAt / 1000 + w.time);
  }

  private find(name: string): Entity | undefined {
    const w = this.h.world;
    for (const e of w.entities.values()) if (e !== w.player && isPlayerLike(e) && e.name === name) return e;
    return undefined;
  }

  readonly commands = {
    friendAdd: (cmd: { name: string }) => {
      const s = this.save;
      const name = cmd.name;
      if (typeof name !== 'string' || !name || name.length > SOCIAL.maxName || name === this.h.world.player.name) return;
      if (s.friends.some((f) => f.name === name)) return this.h.notice(`${name} เป็นเพื่อนอยู่แล้ว`);
      if (relationOf(s, name) === 'enemy') return this.h.notice(`${name} เป็นคู่แค้นของท่าน`);
      if (s.friends.length >= SOCIAL.maxFriends) return this.h.notice('รายชื่อเพื่อนเต็มแล้ว');
      // Offline the simulated players answer at once; nobody by that name here: nothing to ask.
      if (!this.find(name)) return this.h.notice(`ไม่พบผู้เล่นชื่อ ${name} ในบริเวณนี้`);
      this.addFriend(name);
    },
    friendAccept: (cmd: { name: string }) => {
      const s = this.save;
      if (!s.requests.includes(cmd.name)) return;
      s.requests = s.requests.filter((n) => n !== cmd.name);
      if (s.friends.length >= SOCIAL.maxFriends) return this.h.notice('รายชื่อเพื่อนเต็มแล้ว');
      this.addFriend(cmd.name);
    },
    friendDecline: (cmd: { name: string }) => {
      const s = this.save;
      if (!s.requests.includes(cmd.name)) return;
      s.requests = s.requests.filter((n) => n !== cmd.name);
      this.h.emit({ type: 'social', what: 'friendDeclined', name: cmd.name });
    },
    friendRemove: (cmd: { name: string }) => {
      const s = this.save;
      if (!s.friends.some((f) => f.name === cmd.name)) return;
      s.friends = s.friends.filter((f) => f.name !== cmd.name);
      this.h.notice(`ลบ ${cmd.name} ออกจากรายชื่อเพื่อนแล้ว`);
      this.h.emit({ type: 'social', what: 'friendRemoved', name: cmd.name });
    },
    enemyAdd: (cmd: { name: string }) => {
      const s = this.save;
      const at = this.offers.get(cmd.name);
      if (at === undefined || this.h.world.time - at > SOCIAL.offerWindow) return;
      if (s.enemies.some((e) => e.name === cmd.name)) return;
      if (s.enemies.length >= SOCIAL.maxEnemies) return this.h.notice('รายชื่อคู่แค้นเต็มแล้ว');
      this.offers.delete(cmd.name);
      const now = this.clock();
      s.friends = s.friends.filter((f) => f.name !== cmd.name);
      s.enemies.push({ name: cmd.name, since: now, kills: 1, lastAt: now });
      this.h.notice(`จดจำ ${cmd.name} เป็นคู่แค้นแล้ว`);
      this.h.emit({ type: 'social', what: 'enemyAdded', name: cmd.name });
      this.nearT = 0;
    },
    enemyRemove: (cmd: { name: string }) => {
      const s = this.save;
      if (!s.enemies.some((e) => e.name === cmd.name)) return;
      s.enemies = s.enemies.filter((e) => e.name !== cmd.name);
      for (const id of [...this.inView]) if (this.h.world.get(id)?.name === cmd.name) this.inView.delete(id);
      this.h.notice(`ลบ ${cmd.name} ออกจากรายชื่อคู่แค้นแล้ว`);
      this.h.emit({ type: 'social', what: 'enemyRemoved', name: cmd.name });
    },
  };

  private addFriend(name: string): void {
    this.save.friends.push({ name, since: this.clock() });
    this.h.notice(`${name} เป็นเพื่อนของท่านแล้ว`);
    this.h.emit({ type: 'social', what: 'friendAdded', name });
  }

  observe(e: WorldEvent): void {
    const w = this.h.world;
    const p = w.player;
    if (e.type === 'hit' && e.tgtId === p.id && e.srcId !== p.id) {
      const src = w.get(e.srcId);
      if (src && isPlayerLike(src)) this.lastBlow = { id: src.id, name: src.name, red: isRedName(src), at: w.time };
      return;
    }
    if (e.type !== 'death' || e.id !== p.id) return;
    const b = this.lastBlow;
    this.lastBlow = null;
    if (!b || !b.red || w.time - b.at > SOCIAL.blowWindow) return;
    const sworn = this.save.enemies.find((x) => x.name === b.name);
    if (sworn) {
      sworn.kills++;
      sworn.lastAt = this.clock();
      this.h.emit({ type: 'enemyOffer', name: b.name, known: true, kills: sworn.kills });
      return;
    }
    this.offers.set(b.name, w.time);
    this.h.emit({ type: 'enemyOffer', name: b.name, known: false, kills: 1 });
  }

  step(dt: number): void {
    if ((this.nearT -= dt) > 0) return;
    this.nearT = SOCIAL.nearCheck;
    const w = this.h.world;
    const enemies = w.progress.social?.enemies;
    if (!enemies?.length) {
      this.inView.clear();
      return;
    }
    const p = w.player;
    const names = new Set(enemies.map((x) => x.name));
    const out = (SOCIAL.nearRange + SOCIAL.nearSlack) ** 2;
    const inside = SOCIAL.nearRange ** 2;
    for (const id of [...this.inView]) {
      const e = w.get(id);
      if (!e || e.dead || !names.has(e.name) || (e.x - p.x) ** 2 + (e.y - p.y) ** 2 > out) this.inView.delete(id);
    }
    for (const e of w.entities.values()) {
      if (e === p || e.dead || !isPlayerLike(e) || !names.has(e.name) || this.inView.has(e.id)) continue;
      if ((e.x - p.x) ** 2 + (e.y - p.y) ** 2 > inside) continue;
      this.inView.add(e.id);
      this.h.notice(`คู่แค้น ${e.name} อยู่ใกล้ท่าน!`);
      this.h.emit({ type: 'enemyNear', id: e.id, name: e.name, x: e.x, y: e.y });
    }
  }
}
